import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
    Plus, Edit2, Trash2, X, Image as ImageIcon, ShieldCheck, 
    Clock, AlertCircle, CheckCircle2, Globe, DollarSign, Package 
} from 'lucide-react';
import { 
    getProducts, 
    createProduct, 
    updateProduct, 
    deleteProduct, 
    getCategories, 
    getActiveCountries, 
    getVendorApplicationStatus 
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { getTranslation as t, isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

const VendorDashboardPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const rtl = isRTL();

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [countries, setCountries] = useState([]);
    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        nameAr: '',
        description: '',
        descriptionAr: '',
        price: '',
        compareAtPrice: '',
        quantity: 10,
        sku: '',
        categoryId: '',
        countryCode: '',
        images: [''],
        videoUrl: '',
        availableCountries: []
    });

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login?redirect=/vendor/products');
            return;
        }

        const init = async () => {
            setLoading(true);
            try {
                const [appRes, catRes, countryList] = await Promise.all([
                    getVendorApplicationStatus().catch(() => null),
                    getCategories().catch(() => []),
                    getActiveCountries().catch(() => [])
                ]);

                if (appRes && appRes.data) {
                    setApplication(appRes.data);
                }

                if (Array.isArray(catRes)) setCategories(catRes);
                if (Array.isArray(countryList)) setCountries(countryList);

                // Load vendor products
                const userId = localStorage.getItem('userId');
                const prodRes = await getProducts(0, 50, '', '', false, userId);
                if (prodRes && prodRes.data) {
                    setProducts(prodRes.data.content || prodRes.data || []);
                }
            } catch (err) {
                console.error('Failed to load vendor dashboard', err);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [isAuthenticated, navigate]);

    const handleOpenModal = (prod = null) => {
        const defaultCountry = application?.countryCode || 'SA';
        if (prod) {
            setEditingProduct(prod);
            setFormData({
                name: prod.name || '',
                nameAr: prod.nameAr || '',
                description: prod.description || '',
                descriptionAr: prod.descriptionAr || '',
                price: prod.price || '',
                compareAtPrice: prod.compareAtPrice || '',
                quantity: prod.quantity || 1,
                sku: prod.sku || '',
                categoryId: prod.categoryId || (categories[0]?.id || ''),
                countryCode: (prod.availableCountries && prod.availableCountries.length > 0 ? prod.availableCountries[0] : (prod.countryCode || defaultCountry)),
                images: prod.images && prod.images.length > 0 ? prod.images : [''],
                videoUrl: prod.videoUrl || '',
                availableCountries: prod.availableCountries || [defaultCountry]
            });
        } else {
            setEditingProduct(null);
            setFormData({
                name: '',
                nameAr: '',
                description: '',
                descriptionAr: '',
                price: '',
                compareAtPrice: '',
                quantity: 10,
                sku: 'SKU-' + Math.floor(100000 + Math.random() * 900000),
                categoryId: categories[0]?.id || 1,
                countryCode: defaultCountry,
                images: [''],
                videoUrl: '',
                availableCountries: [defaultCountry]
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const targetCountry = (formData.countryCode || application?.countryCode || 'SA').toUpperCase();
            const payload = {
                ...formData,
                price: parseFloat(formData.price) || 0,
                compareAtPrice: formData.compareAtPrice ? parseFloat(formData.compareAtPrice) : null,
                quantity: parseInt(formData.quantity) || 0,
                categoryId: Number(formData.categoryId) || (categories[0]?.id || 1),
                images: formData.images.filter(img => img && img.trim().length > 0),
                countryCode: targetCountry,
                availableCountries: [targetCountry]
            };

            if (editingProduct) {
                await updateProduct(editingProduct.id, payload);
            } else {
                await createProduct(payload);
            }

            setIsModalOpen(false);
            // Refresh products
            const userId = localStorage.getItem('userId');
            const prodRes = await getProducts(0, 50, '', '', false, userId);
            if (prodRes && prodRes.data) {
                setProducts(prodRes.data.content || prodRes.data || []);
            }
        } catch (err) {
            alert(err.response?.data?.message || (rtl ? 'حدث خطأ أثناء حفظ المنتج' : 'Failed to save product'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(rtl ? 'هل أنت متأكد من رغبتك في حذف هذا المنتج؟' : 'Are you sure you want to delete this product?')) return;
        try {
            await deleteProduct(id);
            setProducts(p => p.filter(item => item.id !== id));
        } catch (err) {
            alert(rtl ? 'فشل حذف المنتج' : 'Failed to delete product');
        }
    };

    if (loading) {
        return (
            <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner />
            </div>
        );
    }

    // If no application or not approved, show application prompt
    if (!application || application.status !== 'APPROVED') {
        return (
            <div className="container" style={{ maxWidth: '850px', margin: '80px auto', padding: '0 20px', direction: rtl ? 'rtl' : 'ltr' }}>
                <div style={{
                    background: 'linear-gradient(145deg, #111827 0%, #1c1917 100%)',
                    border: '1px solid #d4af37',
                    borderRadius: '24px',
                    padding: '48px 32px',
                    textAlign: 'center',
                    color: '#fff'
                }}>
                    <div style={{
                        width: '72px', height: '72px', borderRadius: '50%',
                        background: 'rgba(212, 175, 55, 0.15)', border: '2px solid #d4af37',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
                    }}>
                        <ShieldCheck size={36} color="#d4af37" />
                    </div>

                    <h2 style={{ fontSize: '26px', fontWeight: '800', marginBottom: '12px' }}>
                        {application?.status === 'PENDING' || application?.status === 'UNDER_REVIEW'
                            ? (rtl ? 'طلبك كمورد رسمي قيد المراجعة ⏳' : 'Vendor Application Under Review ⏳')
                            : (rtl ? 'انضم إلى شبكة موردي وتجار Luxvono 🌟' : 'Become an Official Verified Vendor 🌟')}
                    </h2>

                    <p style={{ color: '#9ca3af', fontSize: '15px', maxWidth: '540px', margin: '0 auto 28px', lineHeight: '1.6' }}>
                        {application?.status === 'PENDING' || application?.status === 'UNDER_REVIEW'
                            ? (rtl ? 'يقوم فريق الامتثال بالتحقق من السجل التجاري والرقم الضريبي والوثائق الرسمية. سيتم فتح لوحة إضافة المنتجات ونشر البضاعة فور الاعتماد.' : 'Your commercial registration and documents are being reviewed. Product publishing will be unlocked immediately upon approval.')
                            : (rtl ? 'لبدء نشر بضاعتك ومنتجاتك وتحديد أسعارك في دولتك والأسواق العالمية، يرجى تقديم طلب الاعتماد وتعبئة السجل التجاري والرقم الضريبي.' : 'To start selling luxury inventory, set custom pricing and regional visibility, submit your official vendor registration with commercial credentials.')}
                    </p>

                    <Link
                        to="/vendor/apply"
                        style={{
                            background: '#d4af37',
                            color: '#000',
                            padding: '14px 32px',
                            borderRadius: '12px',
                            fontWeight: '700',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {application ? (rtl ? 'عرض حالة الطلب وتحديث الوثائق' : 'View Status & Update Docs') : (rtl ? 'تقديم طلب الاعتماد الآن' : 'Apply for Vendor Account')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="container" style={{ maxWidth: '1200px', margin: '40px auto 80px', padding: '0 20px', direction: rtl ? 'rtl' : 'ltr' }}>
            
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{
                            background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', border: '1px solid #10b981',
                            padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700'
                        }}>
                            ✓ {rtl ? 'مورد معتمد' : 'Verified Vendor'}
                        </span>
                        <span style={{ color: '#d4af37', fontSize: '13px', fontWeight: '600' }}>
                            {application.businessName} ({application.countryCode})
                        </span>
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#fff', margin: 0 }}>
                        {rtl ? 'لوحة تحكم المنتجات والبضاعة' : 'Vendor Inventory & Products'}
                    </h1>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => navigate('/vendor/publish')}
                        style={{
                            background: 'linear-gradient(135deg, #d4af37 0%, #aa820a 100%)', color: '#000', padding: '12px 24px',
                            borderRadius: '12px', fontWeight: '800', border: 'none', cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '15px',
                            boxShadow: '0 4px 16px rgba(212, 175, 55, 0.35)'
                        }}
                    >
                        <Plus size={18} />
                        {rtl ? '✨ استوديو النشر الاحترافي (فيديو ومنتج)' : '✨ Professional Publishing Studio'}
                    </button>
                </div>
            </div>

            {/* Products Table / Grid */}
            {products.length === 0 ? (
                <div style={{
                    background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '20px', padding: '60px 20px', textAlign: 'center', color: '#9ca3af'
                }}>
                    <Package size={48} color="#d4af37" style={{ marginBottom: '16px' }} />
                    <h3 style={{ color: '#fff', fontSize: '18px', marginBottom: '8px' }}>
                        {rtl ? 'لم تقم بنشر أي منتجات بعد' : 'No products published yet'}
                    </h3>
                    <p style={{ fontSize: '14px', maxWidth: '420px', margin: '0 auto 24px' }}>
                        {rtl ? 'ابدأ بإضافة بضاعتك وتحديد السعر والصور ونطاق الدول المستهدفة.' : 'Add your first product, specify price, photos and country targeting.'}
                    </p>
                    <button
                        onClick={() => handleOpenModal()}
                        style={{
                            background: '#d4af37', color: '#000', padding: '10px 24px',
                            borderRadius: '10px', fontWeight: '700', border: 'none', cursor: 'pointer'
                        }}
                    >
                        {rtl ? 'إضافة منتجك الأول' : 'Add First Product'}
                    </button>
                </div>
            ) : (
                <div style={{
                    background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '20px', overflow: 'hidden'
                }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', color: '#fff', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ background: 'rgba(255, 255, 255, 0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <th style={{ padding: '16px', textAlign: rtl ? 'right' : 'left' }}>{rtl ? 'المنتج' : 'Product'}</th>
                                    <th style={{ padding: '16px', textAlign: rtl ? 'right' : 'left' }}>SKU</th>
                                    <th style={{ padding: '16px', textAlign: rtl ? 'right' : 'left' }}>{rtl ? 'السعر' : 'Price'}</th>
                                    <th style={{ padding: '16px', textAlign: rtl ? 'right' : 'left' }}>{rtl ? 'الكمية' : 'Stock'}</th>
                                    <th style={{ padding: '16px', textAlign: rtl ? 'right' : 'left' }}>{rtl ? 'الحالة' : 'Status'}</th>
                                    <th style={{ padding: '16px', textAlign: 'center' }}>{rtl ? 'الإجراءات' : 'Actions'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map(p => (
                                    <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                        <td style={{ padding: '16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <img 
                                                    src={p.primaryImageUrl || (p.images && p.images[0]) || '/placeholder.png'} 
                                                    alt={p.name}
                                                    style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', background: '#222' }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: '600' }}>{p.name}</div>
                                                    <div style={{ color: '#9ca3af', fontSize: '12px' }}>{p.categoryName || p.category}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px', color: '#9ca3af', fontFamily: 'monospace' }}>{p.sku || '-'}</td>
                                        <td style={{ padding: '16px', fontWeight: '700', color: '#d4af37' }}>
                                            {p.price} {p.currencyCode || 'USD'}
                                        </td>
                                        <td style={{ padding: '16px' }}>{p.quantity || 0}</td>
                                        <td style={{ padding: '16px' }}>
                                            {p.active ? (
                                                <span style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                                                    {rtl ? 'معتمد ومباشر' : 'Live / Active'}
                                                </span>
                                            ) : (
                                                <span style={{ background: 'rgba(212, 175, 55, 0.15)', color: '#d4af37', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                                                    {rtl ? 'قيد مراجعة الإدارة' : 'Pending Review'}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                <button 
                                                    onClick={() => handleOpenModal(p)}
                                                    style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(p.id)}
                                                    style={{ background: 'rgba(239,68,68,0.15)', border: 'none', color: '#f87171', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ADD/EDIT PRODUCT MODAL */}
            {isModalOpen && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                    <div style={{
                        background: '#111827', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '24px',
                        maxWidth: '700px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '32px',
                        color: '#fff', direction: rtl ? 'rtl' : 'ltr'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h2 style={{ fontSize: '22px', fontWeight: '700', margin: 0 }}>
                                {editingProduct ? (rtl ? 'تعديل المنتج' : 'Edit Product') : (rtl ? 'إضافة منتج جديد' : 'Add New Product')}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSave}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                    {rtl ? 'اسم المنتج' : 'Product Name'} *
                                </label>
                                <input 
                                    type="text" required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'السعر' : 'Price'} *
                                    </label>
                                    <input 
                                        type="number" step="0.01" required
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'الكمية المتوفرة' : 'Stock Quantity'} *
                                    </label>
                                    <input 
                                        type="number" required
                                        value={formData.quantity}
                                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                        style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'القسم / التصنيف' : 'Category'} *
                                    </label>
                                    <select
                                        value={formData.categoryId}
                                        onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                                        style={{ width: '100%', padding: '12px', background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff' }}
                                    >
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{rtl ? cat.nameAr || cat.name : cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                        {rtl ? 'دولة النشر والعرض' : 'Target Country'} *
                                    </label>
                                    <select
                                        value={formData.countryCode || application?.countryCode || 'SA'}
                                        onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                                        style={{ width: '100%', padding: '12px', background: '#1e293b', border: '1px solid rgba(212, 175, 55, 0.4)', borderRadius: '10px', color: '#fff', fontWeight: '700' }}
                                    >
                                        {countries && countries.length > 0 ? (
                                            countries.map(c => (
                                                <option key={c.countryCode} value={c.countryCode}>
                                                    {c.flagEmoji} {rtl ? c.nativeName || c.countryName : c.countryName} ({c.countryCode})
                                                </option>
                                            ))
                                        ) : (
                                            <option value={application?.countryCode || 'SA'}>
                                                🇸🇦 {application?.countryCode || 'SA'}
                                            </option>
                                        )}
                                    </select>
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                    {rtl ? 'رابط الصورة الرئيسية' : 'Primary Image URL'} *
                                </label>
                                <input 
                                    type="url" required
                                    value={formData.images[0] || ''}
                                    onChange={(e) => setFormData({ ...formData, images: [e.target.value] })}
                                    placeholder="https://..."
                                    style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff' }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                    {rtl ? 'رابط مقطع الفيديو الترويجي للمنتج (اختياري)' : 'Product Video URL (Optional)'}
                                </label>
                                <input 
                                    type="url"
                                    value={formData.videoUrl}
                                    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                                    placeholder="https://... MP4, Video URL"
                                    style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff' }}
                                />
                                <div style={{ fontSize: '12px', color: '#d4af37', marginTop: '4px' }}>
                                    {rtl 
                                        ? `🎥 سيتم مراجعة الفيديو من الإدارة وسيظهر للمتسوقين في دولتك (${application?.countryCode}) فور الاعتماد.` 
                                        : `🎥 Video will be reviewed by admins and visible only to customers in (${application?.countryCode}) upon approval.`}
                                </div>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '6px' }}>
                                    {rtl ? 'الوصف' : 'Description'}
                                </label>
                                <textarea
                                    rows="3"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff' }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)}
                                    style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: '#9ca3af', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer' }}
                                >
                                    {rtl ? 'إلغاء' : 'Cancel'}
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={saving}
                                    style={{ background: '#d4af37', color: '#000', fontWeight: '700', border: 'none', padding: '10px 24px', borderRadius: '10px', cursor: 'pointer' }}
                                >
                                    {saving ? (rtl ? 'جاري الحفظ...' : 'Saving...') : (rtl ? 'حفظ ونشر' : 'Save & Publish')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorDashboardPage;
